// src/componentes/tienda/tiendaPublica/EstadoPedidoPublico.js
// Estado de un pedido para el comprador (carrito y seguimiento): línea de
// tiempo, productos, total y qué hacer para pagar según el modo de cobro.
// Solo pinta la respuesta pública que ya devolvió ordenOperacion; la única
// llamada propia es «Ya pagué» (reportar_pago_publico); la captura va por WhatsApp.
import React, { useState } from 'react';
import { AlertTriangle, Check, Clock3, Copy, CreditCard, Info, MessageCircle, Smartphone } from 'lucide-react';
import { construirUrlWhatsApp } from '../../../dominio/logicaMensajes';
import { operarOrden } from '../../../servicios/ordenServicio';
import { esCobroAutomatico } from '../../../dominio/pasarelas';
import { PagoCulqi, PagoIzipay, PagoTarjeta, PagoYape } from './PagoPasarela';
import { LogoPago } from './LogosPago';
import {
  datosComprobantePedido, etiquetaEstado, indicePasoPedido, METODOS_REPORTABLES, metodosReportables, pasosPedido, pedidoPagado, solesDeCentimos,
} from '../../../dominio/logicaTiendaPublica';
import { nombreArchivoTicket } from '../../../utilidades/pdfTicket';
import { BotonesPdfTicket } from '../../comunes/BotonesPdfTicket';
import { leerMarcaTienda } from '../../../utilidades/carritoLocal';

// Plazo para pagar un pedido manual antes de que se libere su reserva (como el backend).
const PLAZO_PAGO_MS = 24 * 60 * 60 * 1000;
const fechaHora = ms => new Intl.DateTimeFormat('es-PE', { weekday: 'long', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }).format(ms);

const URL_CHECKOUT = /^https:\/\/www\.mercadopago\.com\.pe\/checkout\//;

/** Datos de pago derivados de la respuesta pública. */
export const datosPagoPedido = (resultado) => {
  const checkout = resultado?.pasarela;
  const automatico = esCobroAutomatico(resultado?.modoCobro);
  const prueba = resultado?.modoCobro === 'automatico_pruebas';
  const pendientePagoAutomatico = automatico && resultado.estado === 'pendiente_pago' && resultado.reservaActiva === true;
  const enlacePago = pendientePagoAutomatico && checkout && URL_CHECKOUT.test(checkout.checkoutUrl || '')
    && checkout.vence > Date.now() && ['pendiente', 'pending', 'rejected'].includes(checkout.estado) ? checkout.checkoutUrl : '';
  // Datos para pagar (Ajustes del gym); qrYape: respuestas guardadas por versiones anteriores.
  const cobro = { ...(resultado?.cobroManual || {}) };
  if (!cobro.qr && typeof resultado?.qrYape === 'string') cobro.qr = resultado.qrYape;
  if (!/^(https:\/\/img\.allgym\.app\/|data:image\/(png|jpeg|webp);base64,)/.test(cobro.qr || '')) delete cobro.qr;
  const esperaPagoManual = !automatico && resultado?.estado === 'pendiente_pago' && resultado.reservaActiva === true;
  // Culqi e Izipay se pagan en un formulario del proveedor dentro de la página.
  const proveedor = checkout?.proveedor || resultado?.proveedorCobro || 'mercadopago';
  const pagoEmbebido = pendientePagoAutomatico && ['culqi', 'izipay'].includes(proveedor) && checkout?.vence > Date.now()
    && ['pendiente', 'rejected', '3ds'].includes(checkout?.estado) && Boolean(checkout?.culqi || checkout?.izipay);
  // Mercado Pago dentro de la página (tarjeta y Yape), mientras el intento siga vigente.
  const intentoMpVigente = pendientePagoAutomatico && proveedor === 'mercadopago'
    && checkout?.vence > Date.now() && ['pendiente', 'pending', 'rejected'].includes(checkout?.estado);
  const yape = intentoMpVigente && Boolean(checkout?.yape?.publicKey);
  const tarjeta = intentoMpVigente && Boolean(checkout?.tarjeta?.publicKey || checkout?.yape?.publicKey);
  return { automatico, prueba, proveedor, pendientePagoAutomatico, enlacePago, pagoEmbebido, yape, tarjeta,
    enPagina: yape || tarjeta, cobro, esperaPagoManual };
};

const MEDIOS_MP = {
  tarjeta: { titulo: 'Tarjeta de crédito o débito', detalle: 'En una sola cuota, sin salir de la tienda', logos: ['visa', 'mastercard'] },
  yape: { titulo: 'Yape', detalle: 'Con el código de aprobación de tu app', logos: ['yape'] },
};

/** Elegir tarjeta o Yape y pagar aquí mismo (Mercado Pago, Checkout API). */
// cobrar: { tarjeta, yape } con el caso de uso de cada medio (por defecto, el pedido;
// la mensualidad en línea pasa el suyo). La pantalla es la misma para ambos.
export const PagoEnPagina = ({ gymId, resultado, pago, onActualizado, cobrar = {} }) => {
  const opciones = ['tarjeta', 'yape'].filter((id) => pago[id]);
  const [medio, setMedio] = useState(opciones[0]);
  return <fieldset className="tp-medios-pago">
    <legend>Elige cómo pagar</legend>
    {opciones.map((id) => <div key={id} className={`tp-medio${medio === id ? ' is-activo' : ''}`}>
      <label className="tp-medio-cabecera">
        <input type="radio" name="tp-medio-pago" value={id} checked={medio === id} onChange={() => setMedio(id)} />
        <span className="tp-medio-texto"><strong>{MEDIOS_MP[id].titulo}</strong><small>{MEDIOS_MP[id].detalle}</small></span>
        <span className="tp-logos-pago">{MEDIOS_MP[id].logos.map((logo) => <LogoPago key={logo} medio={logo} />)}</span>
      </label>
      {medio === id && <div className="tp-medio-panel">
        {id === 'tarjeta'
          ? <PagoTarjeta gymId={gymId} resultado={resultado} prueba={pago.prueba} onActualizado={onActualizado} enlaceAlterno={pago.enlacePago}
            cobrar={cobrar.tarjeta || null} />
          : <PagoYape gymId={gymId} resultado={resultado} prueba={pago.prueba} onActualizado={onActualizado} titulo={false}
            cobrar={cobrar.yape || null} />}
      </div>}
    </div>)}
  </fieldset>;
};

const LineaTiempo = ({ estado, entrega }) => {
  const indice = indicePasoPedido(estado);
  if (indice === null) {
    return <p className="tp-aviso tp-aviso--atencion" role="status">
      <AlertTriangle size={16} aria-hidden="true" /><span>{etiquetaEstado(estado)}. Si tienes dudas, escribe al gimnasio.</span>
    </p>;
  }
  return <ol className="tp-linea-tiempo" aria-label="Avance del pedido">
    {pasosPedido(entrega).map((paso, i) => {
      // Lo alcanzado lleva check; el paso siguiente es el que está en curso.
      const fase = i <= indice ? 'hecho' : i === indice + 1 ? 'actual' : 'pendiente';
      return <li key={paso.id} className={`is-${fase}`} aria-current={fase === 'actual' ? 'step' : undefined}>
        <span className="tp-linea-punto" aria-hidden="true">{fase === 'hecho' ? <Check size={12} /> : null}</span>
        <span>{paso.etiqueta}</span>
      </li>;
    })}
  </ol>;
};

const ProductosPedido = ({ items }) => {
  if (!items?.length) return null;
  return <ul className="tp-pedido-items" aria-label="Productos del pedido">
    {items.map((item, i) => {
      const subtotal = Number.isFinite(item.subtotalCentimos) ? item.subtotalCentimos
        : Number.isFinite(item.precioCentimos) ? item.precioCentimos * (item.cantidad || 1) : null;
      return <li key={`${item.nombre}-${i}`}>
        <span><strong>{item.cantidad} × {item.nombre}</strong>
          {(item.talla || item.color) && <small>{[item.talla && `Talla: ${item.talla}`, item.color && `Color: ${item.color}`].filter(Boolean).join(' · ')}</small>}</span>
        {subtotal !== null && <span>{solesDeCentimos(subtotal)}</span>}
      </li>;
    })}
  </ul>;
};

/** Yape, Plin, titular y QR del gimnasio, con copiar número. */
const DatosCobro = ({ cobro }) => {
  const [copiado, setCopiado] = useState('');
  const copiar = async (numero) => {
    try { await navigator.clipboard.writeText(numero); setCopiado(numero); setTimeout(() => setCopiado(''), 2000); } catch { /* sin portapapeles */ }
  };
  const numeros = [['Yape', cobro.yape], ['Plin', cobro.plin]].filter(([, n]) => n);
  if (!numeros.length && !cobro.qr) return <p className="tp-nota">El gimnasio te escribirá por WhatsApp para indicarte cómo pagar.</p>;
  return <div className="tp-datos-cobro">
    {numeros.length > 0 && <ul aria-label="Números para pagar">{numeros.map(([app, numero]) => <li key={app}>
      <span><small>{app}</small><strong>{numero.replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3')}</strong></span>
      <button type="button" className="tp-boton-texto" onClick={() => copiar(numero)}>
        <Copy size={14} aria-hidden="true" />{copiado === numero ? 'Copiado' : `Copiar número de ${app}`}</button>
    </li>)}</ul>}
    {cobro.titular && <p className="tp-nota">A nombre de <strong>{cobro.titular}</strong>. Verifica el nombre antes de pagar.</p>}
    {cobro.qr && <img className="tp-qr" src={cobro.qr} alt="QR para pagar al gimnasio" loading="lazy" />}
  </div>;
};

/**
 * «Ya pagué»: el comprador avisa con qué pagó (el gimnasio recibe un aviso y el
 * pedido queda «Pago por verificar») y luego manda su captura por WhatsApp, como
 * ya se acostumbra. La app no guarda imágenes ni datos del pago del cliente.
 */
const ReportarPago = ({ gymId, resultado, metodosPago, onActualizado, whatsapp, nombreTienda }) => {
  const metodos = metodosReportables(metodosPago);
  const [metodo, setMetodo] = useState(resultado.pagoReportado?.metodo || metodos[0]);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState('');
  const [corrigiendo, setCorrigiendo] = useState(false);
  const reportado = resultado.pagoReportado;
  const intentos = Number(reportado?.intentos) || 0;

  const enviar = async (evento) => {
    evento.preventDefault();
    if (enviando) return;
    setEnviando(true); setError('');
    try {
      // Con los datos de cobro ya guardados no se vuelven a pedir (ahorra la lectura del gym).
      const res = await operarOrden({ accion: 'reportar_pago_publico', gymId, idempotencyKey: resultado.claveConsulta,
        metodo, ...(resultado.cobroManual ? { cobroEnCache: true } : {}) });
      onActualizado(res); setCorrigiendo(false);
    } catch (e) {
      setError(e?.message && !/internal/i.test(e.message) ? e.message : 'No se pudo enviar el aviso. Inténtalo de nuevo en unos minutos.');
    } finally { setEnviando(false); }
  };

  if (reportado && !corrigiendo) {
    const app = METODOS_REPORTABLES[reportado.metodo] || reportado.metodo;
    const mensaje = `Hola${nombreTienda ? ` ${nombreTienda}` : ''}, ya pagué el pedido ${resultado.numeroPedido || ''} por ${app} (${solesDeCentimos(resultado.totalCentimos)}). Te envío la captura.`;
    const enlace = whatsapp ? construirUrlWhatsApp(whatsapp, mensaje, '') : '';
    return <div className="tp-bloque-pago">
      <div className="tp-aviso tp-aviso--exito" role="status">
        <Check size={16} aria-hidden="true" />
        <span>Avisamos al gimnasio que pagaste por {app}{reportado.referencia ? ` (N.º ${reportado.referencia})` : ''}.
          {enlace ? ' Ahora envíale la captura por WhatsApp: lo verificará y aquí verás cuando lo confirme.' : ' Lo verificará y aquí verás cuando lo confirme.'}</span>
      </div>
      {enlace && <a className="tp-boton tp-boton--whatsapp tp-boton--ancho" href={enlace} target="_blank" rel="noreferrer">
        <MessageCircle size={18} aria-hidden="true" /> Enviar captura por WhatsApp</a>}
      {intentos < 3 && <button type="button" className="tp-boton-texto" onClick={() => setCorrigiendo(true)}>Me equivoqué de medio de pago</button>}
    </div>;
  }

  return <form className="tp-reportar-pago" onSubmit={enviar} noValidate>
    <h3>¿Ya pagaste?</h3>
    <p>Avísanos con qué pagaste. Después envías la captura por WhatsApp y el gimnasio te confirma aquí.</p>
    {metodos.length > 1 && <fieldset className="tp-reportar-metodos">
      <legend>Pagaste con</legend>
      {metodos.map((m) => <label key={m} className={metodo === m ? 'is-activo' : ''}>
        <input type="radio" name="metodo-reporte" value={m} checked={metodo === m} onChange={() => setMetodo(m)} />{METODOS_REPORTABLES[m]}
      </label>)}
    </fieldset>}
    {error && <p role="alert" className="tp-error">{error}</p>}
    <button type="submit" className="tp-boton tp-boton--primario" disabled={enviando}>
      {enviando ? 'Enviando…' : 'Ya pagué'}
    </button>
  </form>;
};

const TEXTO_PASARELA = {
  culqi: [
    'Culqi en modo de pruebas: usa una tarjeta de prueba de Culqi. Tu stock ya está reservado.',
    'Paga con tarjeta, Yape o billetera en Culqi. Tu stock ya está reservado; el pedido se confirma cuando Culqi aprueba el pago.'],
  izipay: [
    'Izipay en modo de pruebas: usa una tarjeta de prueba de Izipay. Tu stock ya está reservado.',
    'Paga con tarjeta, Yape o Plin en Izipay. Tu stock ya está reservado; el pedido se confirma cuando Izipay aprueba el pago.'],
  mercadopago: [
    'Mercado Pago en modo de pruebas: usa solo el comprador y la tarjeta de prueba. Tu stock ya está reservado.',
    'Paga de forma segura en Mercado Pago. Tu stock ya está reservado; el gimnasio confirma cuando Mercado Pago aprueba el pago.'],
};

const TEXTO_EN_PAGINA = [
  'Mercado Pago en modo de pruebas: paga aquí mismo con la tarjeta o el Yape de prueba. Tu stock ya está reservado.',
  'Paga aquí mismo de forma segura con Mercado Pago. Tu stock ya está reservado y el pedido se confirma al aprobarse el pago.'];

export const EstadoPedidoPublico = ({ gymId, resultado, metodosPago, ocupado, onRecuperarPago, onActualizado, nombreTienda, whatsapp }) => {
  const pago = datosPagoPedido(resultado);
  const entrega = resultado.entrega || resultado.compradorMensaje?.entrega || 'retiro';
  const items = resultado.items?.length ? resultado.items : resultado.lineasPedido;

  return <div className="tp-estado-pedido">
    <LineaTiempo estado={resultado.estado || 'pendiente_validacion'} entrega={entrega} />
    <ProductosPedido items={items} />
    {resultado.envio && <p className="tp-pedido-envio">
      Envío a {[resultado.envio.provincia, resultado.envio.departamento].filter(Boolean).join(', ')} por {resultado.envio.agencia || 'agencia'}.
      {resultado.envio.guia ? <> N.º de guía: <strong>{resultado.envio.guia}</strong>. Recoge con tu DNI.</> : ' Te avisaremos el número de guía al enviarlo.'}
    </p>}
    <div className="tp-pedido-total">
      <span>Total</span><strong>{solesDeCentimos(resultado.totalCentimos)}</strong>
    </div>
    <p className="tp-pedido-pago-estado">Pago: {etiquetaEstado(resultado.estadoPago || 'pendiente')}</p>
    {/* Pagado: comprobante interno en PDF, armado aquí mismo (sin consultas extra). */}
    {pedidoPagado(resultado.estadoPago) && items?.length > 0 && <div className="tp-comprobante-pdf">
      <BotonesPdfTicket datos={datosComprobantePedido({ ...resultado, items }, { nombreTienda, logo: leerMarcaTienda(gymId)?.logo || '' })} etiqueta="comprobante (PDF)"
        claseBoton="tp-boton tp-boton--secundario" nombreArchivo={nombreArchivoTicket('comprobante', nombreTienda, resultado.numeroPedido || resultado.id)} />
    </div>}

    {pago.automatico ? <div className="tp-bloque-pago">
      <p className="tp-aviso tp-aviso--info"><CreditCard size={16} aria-hidden="true" />
        <span>{pago.enPagina ? TEXTO_EN_PAGINA[pago.prueba ? 0 : 1] : (TEXTO_PASARELA[pago.proveedor] || TEXTO_PASARELA.mercadopago)[pago.prueba ? 0 : 1]}</span></p>
      {pago.enPagina && resultado.claveConsulta && <PagoEnPagina gymId={gymId} resultado={resultado} pago={pago} onActualizado={onActualizado} />}
      {pago.pagoEmbebido && resultado.claveConsulta && (pago.proveedor === 'culqi'
        ? <PagoCulqi gymId={gymId} resultado={resultado} nombreTienda={nombreTienda} onActualizado={onActualizado} />
        : <PagoIzipay gymId={gymId} resultado={resultado} onActualizado={onActualizado} />)}
      {pago.pendientePagoAutomatico && resultado.pasarela?.estado === 'rejected' && resultado.pasarela?.mensaje
        && <p className="tp-nota" role="status">Último intento: {resultado.pasarela.mensaje}</p>}
      {pago.pendientePagoAutomatico && pago.proveedor !== 'mercadopago' && resultado.pasarela?.estado === 'revision'
        && <p className="tp-nota">Tu pago quedó en revisión. No vuelvas a pagar: el gimnasio lo confirmará con la pasarela.</p>}
      {/* Sin formulario en la página (gym sin Public Key): la página de Mercado Pago, en la misma pestaña. */}
      {pago.enPagina ? null : pago.enlacePago
        ? <a className="tp-boton tp-boton--primario tp-boton--ancho" href={pago.enlacePago}>
          {pago.prueba ? 'Continuar al pago de prueba' : 'Continuar al pago'}</a>
        : pago.pendientePagoAutomatico && pago.proveedor === 'mercadopago'
          ? <p className="tp-nota">Consulta el estado antes de intentar pagar de nuevo. Volver desde Mercado Pago no confirma por sí solo el cobro.</p>
          : null}
      {pago.pendientePagoAutomatico && !pago.enlacePago && !pago.pagoEmbebido && !pago.enPagina && (pago.proveedor === 'mercadopago' || resultado.pasarela?.estado !== 'revision') && resultado.claveConsulta && <>
        <button type="button" className="tp-boton tp-boton--secundario tp-boton--ancho" disabled={ocupado} onClick={onRecuperarPago}>Recuperar enlace de pago</button>
        <p className="tp-nota">Se conserva el mismo pedido. Si el intento anterior quedó sin confirmar, no se genera otro enlace: escribe al gimnasio.</p>
      </>}
    </div>
      : pago.esperaPagoManual ? <div className="tp-bloque-pago">
        <p className="tp-aviso tp-aviso--info"><Smartphone size={16} aria-hidden="true" />
          <span>Tus productos están reservados. Paga <strong>{solesDeCentimos(resultado.totalCentimos)}</strong>
            {Number.isFinite(resultado.reservadoEn) && !resultado.pagoReportado ? ` antes del ${fechaHora(resultado.reservadoEn + PLAZO_PAGO_MS)}` : ''} y avísanos.</span></p>
        {!resultado.pagoReportado && <DatosCobro cobro={pago.cobro} />}
        {resultado.claveConsulta && metodosReportables(metodosPago).length > 0
          && <ReportarPago gymId={gymId} resultado={resultado} metodosPago={metodosPago} onActualizado={onActualizado} whatsapp={whatsapp} nombreTienda={nombreTienda} />}
        {!resultado.claveConsulta && resultado.soloLectura && <p className="tp-nota">
          Después de pagar, envía la captura al gimnasio por WhatsApp o avísalo desde «Mis pedidos» en el celular donde hiciste el pedido.</p>}
      </div>
        : (resultado.estado === 'pendiente_validacion' || !resultado.estado)
          ? <p className="tp-aviso tp-aviso--neutro"><Clock3 size={16} aria-hidden="true" />
            <span>No pagues todavía: el gimnasio confirmará el stock y el total y te avisará cómo pagar.</span></p>
          : <p className="tp-aviso tp-aviso--neutro"><Info size={16} aria-hidden="true" />
            <span>Coordina con el gimnasio la entrega o la devolución. No repitas el pago.</span></p>}
  </div>;
};
